import { breadcrumbSlot } from "@redotech/arbiter/breadcrumb";
import { t } from "@redotech/redo-i18n";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { shopperAgentEnabled } from "@redotech/redo-merchant-app-common/shopper-agent-enabled";
import {
  PlaybookChannel,
  PlaybookPage,
} from "@redotech/redo-merchant-marketing-app/common/shopper-agent/playbook-routes";
import { memo } from "react";
import { matchPath, Navigate, useLocation } from "react-router-dom";

const capabilityBreadcrumb = breadcrumbSlot();

/** Keeps `?setting=` so a deep link to the playbook root still scrolls. */
const RedirectToPlaybookBrand = memo(function RedirectToPlaybookBrand() {
  const { search } = useLocation();
  return <Navigate replace to={{ pathname: PlaybookPage.BRAND, search }} />;
});

function hidePadding(url: string): boolean {
  return (
    !!matchPath("/stores/:storeId/commerce-agent/playbook/*", url) ||
    !!matchPath("/stores/:storeId/commerce-agent/calendar", url) ||
    !!matchPath("/stores/:storeId/commerce-agent/conversations", url) ||
    !!matchPath("/stores/:storeId/commerce-agent/knowledge", url) ||
    !!matchPath("/stores/:storeId/commerce-agent/sandbox", url) ||
    !!matchPath(
      "/stores/:storeId/commerce-agent/strategies/:strategyId",
      url,
    ) ||
    !!matchPath("/stores/:storeId/commerce-agent/settings", url)
  );
}

export const shopperAgentRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      breadcrumb: "Commerce Agent",
      permission: ({ team }) => shopperAgentEnabled(team),
    },
    async lazy() {
      const { Page } = await import("../../page");
      return { element: <Page hidePadding={hidePadding} /> };
    },
    children: [
      {
        async lazy() {
          const { ConciergeAskRedoRouteContext } =
            await import("../../../concierge/concierge-ask-redo-route-context");
          return { Component: ConciergeAskRedoRouteContext };
        },
        children: [
          {
            handle: { breadcrumb: "Dashboard" },
            index: true,
            async lazy() {
              const { ConciergeIndexPage } =
                await import("../../../concierge/concierge-index-page");
              return { Component: ConciergeIndexPage };
            },
          },
          {
            path: "conversations",
            handle: { breadcrumb: "Conversations" },
            async lazy() {
              const { ShopperAgentConversationsPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-shopper-agent-app/conversations/shopper-agent-conversations-page");
              return { Component: ShopperAgentConversationsPage };
            },
          },
          {
            path: "analytics",
            handle: { breadcrumb: "Analytics" },
            async lazy() {
              const { TextConciergeAnalyticsPage } =
                await import("@redotech/redo-merchant-marketing-app/text-concierge/analytics/text-concierge-analytics-page");
              return { Component: TextConciergeAnalyticsPage };
            },
          },
          {
            path: "calendar",
            handle: { breadcrumb: "Calendar" },
            async lazy() {
              const { ConciergeCalendarPage } =
                await import("../../../concierge/calendar/concierge-calendar-page");
              return { Component: ConciergeCalendarPage };
            },
          },
          {
            path: "sandbox",
            handle: { breadcrumb: "Sandbox" },
            async lazy() {
              const { ConciergeSandboxPage } =
                await import("../../../concierge/sandbox/concierge-sandbox-page");
              return { Component: ConciergeSandboxPage };
            },
          },
          {
            path: "knowledge",
            handle: { breadcrumb: "Knowledge" },
            async lazy() {
              const { AgentKnowledgePage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-agent-knowledge-app/page");
              return { Component: AgentKnowledgePage };
            },
          },
          {
            path: "strategies/:strategyId",
            handle: { breadcrumb: "Strategy" },
            async lazy() {
              const { ShopperAgentStrategyRoute } =
                await import("../../../marketing/automations/shopper-agent-strategy-editor");
              return { Component: ShopperAgentStrategyRoute };
            },
          },
          {
            path: "playbook",
            handle: {
              get breadcrumb() {
                return t("Playbook", { context: "Commerce Agent breadcrumb" });
              },
            },
            async lazy() {
              const { ShopperAgentPlaybookLayout } =
                await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/shopper-agent-playbook-layout");
              return { Component: ShopperAgentPlaybookLayout };
            },
            children: [
              { index: true, Component: RedirectToPlaybookBrand },
              {
                path: PlaybookPage.BRAND,
                handle: {
                  get breadcrumb() {
                    return t("Brand", { context: "Commerce Agent breadcrumb" });
                  },
                },
                async lazy() {
                  const { PlaybookBrandPage } =
                    await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-brand-page");
                  return { Component: PlaybookBrandPage };
                },
              },
              {
                path: PlaybookPage.CAPABILITIES,
                handle: {
                  get breadcrumb() {
                    return t("Capabilities", {
                      context: "Commerce Agent breadcrumb",
                    });
                  },
                },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { PlaybookCapabilitiesPage } =
                        await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-capabilities-page");
                      return { Component: PlaybookCapabilitiesPage };
                    },
                  },
                  {
                    path: ":kind",
                    handle: { breadcrumb: capabilityBreadcrumb },
                    async lazy() {
                      const { PlaybookCapabilityPage } =
                        await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-capabilities-page");
                      return {
                        element: (
                          <PlaybookCapabilityPage
                            breadcrumb={capabilityBreadcrumb}
                          />
                        ),
                      };
                    },
                  },
                ],
              },
              {
                path: PlaybookPage.GUARDRAILS,
                handle: {
                  get breadcrumb() {
                    return t("Guardrails", {
                      context: "Commerce Agent breadcrumb",
                    });
                  },
                },
                async lazy() {
                  const { PlaybookGuardrailsPage } =
                    await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-guardrails-page");
                  return { Component: PlaybookGuardrailsPage };
                },
              },
              {
                path: PlaybookPage.CHANNELS,
                handle: {
                  get breadcrumb() {
                    return t("Channels", {
                      context: "Commerce Agent breadcrumb",
                    });
                  },
                },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { PlaybookChannelsPage } =
                        await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-channels-page");
                      return { Component: PlaybookChannelsPage };
                    },
                  },
                  {
                    path: PlaybookChannel.EMAIL,
                    handle: {
                      get breadcrumb() {
                        return t("Email", {
                          context: "Commerce Agent breadcrumb",
                        });
                      },
                    },
                    async lazy() {
                      const { PlaybookEmailChannelPage } =
                        await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-channels-page");
                      return { Component: PlaybookEmailChannelPage };
                    },
                  },
                  {
                    path: PlaybookChannel.SMS,
                    handle: {
                      get breadcrumb() {
                        return t("SMS", {
                          context: "Commerce Agent breadcrumb",
                        });
                      },
                    },
                    async lazy() {
                      const { PlaybookSmsChannelPage } =
                        await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-channels-page");
                      return { Component: PlaybookSmsChannelPage };
                    },
                  },
                  {
                    path: PlaybookChannel.RCS,
                    handle: {
                      get breadcrumb() {
                        return t("RCS", {
                          context: "Commerce Agent breadcrumb",
                        });
                      },
                    },
                    async lazy() {
                      const { PlaybookRcsChannelPage } =
                        await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-channels-page");
                      return { Component: PlaybookRcsChannelPage };
                    },
                  },
                  {
                    path: PlaybookChannel.IMESSAGE,
                    handle: {
                      get breadcrumb() {
                        return t("iMessage", {
                          context: "Commerce Agent breadcrumb",
                        });
                      },
                    },
                    async lazy() {
                      const { PlaybookIMessageChannelPage } =
                        await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-channels-page");
                      return { Component: PlaybookIMessageChannelPage };
                    },
                  },
                  {
                    path: PlaybookChannel.RECOVER,
                    handle: { breadcrumb: "Recover" },
                    async lazy() {
                      const { PlaybookRecoverChannelPage } =
                        await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-shopper-agent/playbook/playbook-channels-page");
                      return { Component: PlaybookRecoverChannelPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
